import { Controller } from "@hotwired/stimulus"

// Rich-text editor for the event regulation admin form (#542, Option A).
//
// Trix is the editor, but its serialized output does not match the server's
// allowlist (Events::ShowSerializer::RULES_ALLOWED_TAGS = p br strong em b i u
// ul ol li h2 h3 h4 blockquote). Trix emits <div> for default blocks and <h1>
// for headings — neither is allowlisted, so sanitize() would strip them and
// collapse the formatting. This controller bridges the gap WITHOUT touching the
// server: Trix edits a private source <input>; on every change we normalize its
// HTML (div->p, h1->h2) into the submitted hidden field. The server-side
// sanitizer stays the single security authority — this is UX fidelity only.
//
// Storage is unchanged: the submitted field is still
// event[rules_content][locale], persisted to the jsonb-per-locale column.
export default class extends Controller {
  static targets = ["editor", "source", "output"]

  connect() {
    this._onInitialize = () => this.seed()
    this._onChange = () => this.sync()
    this._onFileAccept = (event) => event.preventDefault()

    this.editorTarget.addEventListener("trix-initialize", this._onInitialize)
    this.editorTarget.addEventListener("trix-change", this._onChange)
    // No attachments: the regulation is text-only and <figure> is not
    // allowlisted anyway. Block drag/drop + paste of files outright.
    this.editorTarget.addEventListener("trix-file-accept", this._onFileAccept)

    // Trix may have initialized before this controller connected (turbo cache,
    // re-render). Seed immediately in that case.
    if (this.editorTarget.editor) this.seed()
  }

  disconnect() {
    this.editorTarget.removeEventListener("trix-initialize", this._onInitialize)
    this.editorTarget.removeEventListener("trix-change", this._onChange)
    this.editorTarget.removeEventListener("trix-file-accept", this._onFileAccept)
  }

  // Load the stored content (held in the submitted field's initial value) into
  // Trix. Legacy plain-text content is converted to HTML so newlines survive.
  seed() {
    if (this._seeded || !this.editorTarget.editor) return
    this._seeded = true
    this.editorTarget.editor.loadHTML(this.prepareSeed(this.outputTarget.value))
    this.sync()
  }

  // Mirror Trix's serialized output (in the source input) into the submitted
  // field, normalized to the server allowlist.
  sync() {
    this.outputTarget.value = this.normalize(this.sourceTarget.value)
  }

  // Stored content -> HTML Trix can load. Already-HTML stays as-is; legacy plain
  // text becomes paragraphs/<br> with its special chars escaped.
  prepareSeed(stored) {
    const text = (stored || "").trim()
    if (text === "") return ""
    if (/<[a-z][\s\S]*>/i.test(text)) return text
    return text
      .split(/\n{2,}/)
      .map((block) => `<p>${this.escapeHtml(block).replace(/\n/g, "<br>")}</p>`)
      .join("")
  }

  // Trix output -> allowlist-safe HTML. div->p, h1->h2; everything else Trix
  // emits (strong/em/ul/ol/li/blockquote/br) already matches the allowlist, and
  // anything outside it is stripped server-side regardless.
  normalize(html) {
    const template = document.createElement("template")
    template.innerHTML = html || ""
    template.content.querySelectorAll("h1").forEach((node) => this.rename(node, "h2"))
    template.content.querySelectorAll("div").forEach((node) => this.rename(node, "p"))
    return template.innerHTML.trim()
  }

  rename(node, tag) {
    const replacement = document.createElement(tag)
    while (node.firstChild) replacement.appendChild(node.firstChild)
    node.replaceWith(replacement)
  }

  escapeHtml(value) {
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }
}
